import React from 'react';
import QRCode from 'react-qr-code';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  OutlinedInput,
  TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import QrCodeIcon from '@mui/icons-material/QrCode';

import type {
  IClinic,
  IClinicFbSite,
  IClinicFbSiteDoctor,
} from '@app/interfaces/clinic.interface';
import type { IDoctor } from '@app/interfaces/doctor.interface';
import { fetchDoctorsByClinic } from '@app/api/doctor';
import { updateClinicFbSites } from '@app/api/clinic';
import { IFeedbackSite } from '@app/interfaces/feedback-site.interface';

const domain =
  process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://fb.respect-m.ru';

type Props = {
  feedbackSite: IFeedbackSite;
  clinic: IClinic;
  setClinic: (clinic: IClinic) => void;
  isOpen: boolean;
  onClose: () => void;
};

const EMPTY_DOCTOR = {
  id: '',
  url: '',
  comment: '',
  qrcodeUrl: '',
};

function EditDoctorListDialog({ feedbackSite, clinic, setClinic, isOpen, onClose }: Props) {
  const [clinicDoctors, setClinicDoctors] = React.useState<IDoctor[]>([]);
  const [doctors, setDoctors] = React.useState<IClinicFbSiteDoctor[]>([]);
  const [editedIndex, setEditedIndex] = React.useState<number | null>(null);
  const [formDoctor, setFormDoctor] = React.useState(EMPTY_DOCTOR);
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [qrcode, setQrcode] = React.useState('');

  const clinicFbSite = clinic.fbSites?.find(({ id }) => id === feedbackSite?.id);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    setDoctors(clinicFbSite?.doctors || []);
    setEditedIndex(null);
    setFormDoctor(EMPTY_DOCTOR);
    setIsFormOpen(false);

    if (clinic.doctors?.length) {
      setClinicDoctors(clinic.doctors);
      return;
    }

    fetchDoctorsByClinic(clinic.id).then((data: IDoctor[]) => setClinicDoctors(data || []));
  }, [isOpen, clinic, clinicFbSite]);

  const getDoctorName = (doctorId: number) => {
    const doctor = clinicDoctors.find(({ id }) => id === doctorId);

    return [doctor?.fio, doctor?.profession].filter(Boolean).join(' | ') || `Доктор #${doctorId}`;
  };

  const getUpdatedFbSites = (updatedDoctors: IClinicFbSiteDoctor[]) => {
    const fbSites = clinic.fbSites || [];
    const hasFeedbackSite = fbSites.some(({ id }) => id === feedbackSite.id);
    const updatedFbSite: IClinicFbSite = {
      id: feedbackSite.id!,
      url: clinicFbSite?.url || '',
      enabled: Boolean(clinicFbSite?.enabled),
      doctorFeedbackEnabled: Boolean(clinicFbSite?.doctorFeedbackEnabled),
      doctors: updatedDoctors,
    };

    return hasFeedbackSite
      ? fbSites.map((fbSite) =>
          fbSite.id === feedbackSite.id ? { ...fbSite, doctors: updatedDoctors } : fbSite
        )
      : [...fbSites, updatedFbSite];
  };

  const saveDoctors = async (updatedDoctors: IClinicFbSiteDoctor[]) => {
    const updatedClinic = await updateClinicFbSites(clinic.id, getUpdatedFbSites(updatedDoctors));

    if (updatedClinic) {
      setClinic(updatedClinic);
      setDoctors(updatedDoctors);
    }
  };

  const startCreateDoctor = () => {
    setEditedIndex(null);
    setFormDoctor(EMPTY_DOCTOR);
    setIsFormOpen(true);
  };

  const startEditDoctor = (doctor: IClinicFbSiteDoctor, index: number) => {
    setEditedIndex(index);
    setFormDoctor({
      id: doctor.id.toString(),
      url: doctor.url || '',
      comment: doctor.comment || '',
      qrcodeUrl: doctor.qrcodeUrl || '',
    });
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setEditedIndex(null);
    setFormDoctor(EMPTY_DOCTOR);
    setIsFormOpen(false);
  };

  const handleDoctorChange = (event: SelectChangeEvent) => {
    setFormDoctor((doctor) => ({
      ...doctor,
      id: event.target.value,
    }));
  };

  const handleQrcodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormDoctor((doctor) => ({
      ...doctor,
      qrcodeUrl: event.target.value,
    }));
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormDoctor((doctor) => ({
      ...doctor,
      url: event.target.value,
    }));
  };

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormDoctor((doctor) => ({
      ...doctor,
      comment: event.target.value,
    }));
  };

  const handleQrcodeClick = () => {
    if (!formDoctor.id || !formDoctor.url) {
      return;
    }

    setFormDoctor((doctor) => ({
      ...doctor,
      qrcodeUrl: `${domain}/qrcode/${doctor.id}`,
    }));
  };

  const saveDoctor = async () => {
    if (!formDoctor.id) {
      return;
    }

    const doctorId = +formDoctor.id;
    const updatedDoctor: IClinicFbSiteDoctor = {
      id: doctorId,
      url: formDoctor.url,
      comment: formDoctor.comment,
      qrcodeUrl: formDoctor.qrcodeUrl,
    };
    const updatedDoctors =
      editedIndex !== null
        ? doctors.map((doctor, index) => (index === editedIndex ? updatedDoctor : doctor))
        : doctors.some(({ id }) => id === doctorId)
          ? doctors.map((doctor) => (doctor.id === doctorId ? updatedDoctor : doctor))
          : [...doctors, updatedDoctor];

    await saveDoctors(updatedDoctors);
    closeForm();
  };

  const deleteDoctor = async (doctorId: number) => {
    await saveDoctors(doctors.filter(({ id }) => id !== doctorId));
    closeForm();
  };

  const handleDoctorFeedbackToggle = async () => {
    if (!feedbackSite?.id) {
      return;
    }

    const fbSites = clinic.fbSites || [];
    const updatedFbSites = clinicFbSite
      ? fbSites.map((fbSite) =>
          fbSite.id === feedbackSite.id
            ? {
                ...fbSite,
                doctorFeedbackEnabled: !fbSite.doctorFeedbackEnabled,
              }
            : fbSite
        )
      : [
          ...fbSites,
          {
            id: feedbackSite.id,
            url: '',
            enabled: false,
            doctorFeedbackEnabled: true,
            doctors,
          },
        ];
    const updatedClinic = await updateClinicFbSites(clinic.id, updatedFbSites);

    if (updatedClinic) {
      setClinic(updatedClinic);
    }
  };

  const selectedDoctorIds = doctors.map(({ id }) => id.toString());
  const isDoctorSelected = (doctorId: string) =>
    selectedDoctorIds.includes(doctorId) && doctorId !== formDoctor.id;

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} maxWidth='sm' fullWidth={true}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingRight: '24px',
          }}
        >
          <DialogTitle>{clinic.name} | Доктора</DialogTitle>
          <Switch
            edge='end'
            onChange={handleDoctorFeedbackToggle}
            checked={Boolean(clinicFbSite?.doctorFeedbackEnabled)}
          />
        </Box>

        <DialogContent>
          <List dense disablePadding sx={{ mb: 2 }}>
            {doctors.map((doctor, index) => (
              <ListItem
                key={doctor.id}
                disableGutters
                secondaryAction={
                  <Box>
                    <IconButton size='small' onClick={() => startEditDoctor(doctor, index)}>
                      <EditIcon fontSize='small' color='primary' />
                    </IconButton>
                    <IconButton size='small' onClick={() => deleteDoctor(doctor.id)}>
                      <DeleteIcon fontSize='small' color='error' />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemText primary={getDoctorName(doctor.id)} secondary={doctor.qrcodeUrl} />
              </ListItem>
            ))}
          </List>

          {isFormOpen ? (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id='feedback-site-doctor-select-label'>Доктор</InputLabel>
                <Select
                  labelId='feedback-site-doctor-select-label'
                  label='Доктор'
                  value={formDoctor.id}
                  onChange={handleDoctorChange}
                >
                  {clinicDoctors.map((doctor) => {
                    const doctorId = doctor.id.toString();

                    return (
                      <MenuItem
                        key={doctor.id}
                        value={doctorId}
                        disabled={isDoctorSelected(doctorId)}
                      >
                        {getDoctorName(doctor.id)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                required
                label='Ссылка'
                value={formDoctor.url}
                onChange={handleUrlChange}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label='Комментарий'
                value={formDoctor.comment}
                onChange={handleCommentChange}
                sx={{ mb: 2 }}
              />

              <FormControl variant='outlined' fullWidth>
                <InputLabel htmlFor='qrcode-input'>QR Code</InputLabel>
                <OutlinedInput
                  fullWidth
                  id='qrcode-input'
                  value={formDoctor.qrcodeUrl}
                  onChange={handleQrcodeChange}
                  endAdornment={
                    <InputAdornment position='end'>
                      <Stack direction='row' spacing={1}>
                        <IconButton
                          aria-label='generate qrcode url'
                          onClick={handleQrcodeClick}
                          disabled={!formDoctor.id}
                          edge='end'
                        >
                          <AutoFixHighIcon color={formDoctor.id ? 'success' : 'disabled'} />
                        </IconButton>
                        <IconButton
                          aria-label='show qrcode url'
                          onClick={() => setQrcode(formDoctor.qrcodeUrl)}
                          edge='end'
                          disabled={!formDoctor.qrcodeUrl}
                        >
                          <QrCodeIcon color={formDoctor.qrcodeUrl ? 'primary' : 'disabled'} />
                        </IconButton>
                      </Stack>
                    </InputAdornment>
                  }
                  label='QR Code'
                />
              </FormControl>
            </>
          ) : null}
        </DialogContent>

        <DialogActions>
          {isFormOpen ? (
            <>
              <Button onClick={closeForm}>Отмена</Button>
              <Button
                variant='contained'
                onClick={saveDoctor}
                disabled={!formDoctor.id || !formDoctor.url}
              >
                Сохранить
              </Button>
            </>
          ) : (
            <Button onClick={startCreateDoctor}>Добавить</Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={!!qrcode} onClose={() => setQrcode('')}>
        <DialogTitle>{formDoctor.id ? getDoctorName(+formDoctor.id) : 'QR Code'}</DialogTitle>
        <DialogContent dividers>
          <QRCode
            size={256}
            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
            value={qrcode}
            viewBox={`0 0 256 256`}
          />
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={() => setQrcode('')}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default EditDoctorListDialog;
